import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { BUNDLE_CREATE } from '@/components/organisms/Modals/ModalConductor';
import { TileSkeleton } from '@/components/organisms/Tile';
import AddNew from '@/components/organisms/Tile/AddNew';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import TileBundle from '@/modules/bundles/components/TileBundle/TileBundle';
import { useBundles } from '@/modules/bundles/hooks/useBundles';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import CreateBundleTile from './CreateBundleTile';

const Bundles = () => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();
  const { bundles } = useBundles(deskId);
  const { desks } = useDesks();
  const desk = desks?.find((desk) => desk.desk_id === deskId);
  useTitle(t('pages.context_bundles', { 0: desk?.name ?? '-' }));

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'bundles', actions.WRITE)
  );

  useSegments(PageName.BUNDLES);

  const showCreateBundleModal = () => {
    dispatch(pushModal(BUNDLE_CREATE));
  };

  function renderTiles() {
    return bundles?.length > 0 ? (
      bundles?.map((bundle) => (
        <TileBundle bundle={bundle} slug={slug} key={bundle.bundle_id} deskId={deskId} />
      ))
    ) : (
      <CreateBundleTile onClick={showCreateBundleModal} readOnly={!canWrite} />
    );
  }

  function renderSkeletons(n = 3) {
    return (
      Array(n)
        .fill(0)
        // eslint-disable-next-line react/no-array-index-key
        .map((_, i) => <TileSkeleton key={i} />)
    );
  }

  return (
    <PageContentWrapper>
      <SettingsContent className="max-w-(--global-container-width)">
        <ContextualHelp title={t('bundles.help_title')} name="rules-help" size="full">
          {t('bundles.help_body')}
        </ContextualHelp>
        <div className="flex flex-col">
          {!bundles && renderSkeletons(3)}
          {bundles && renderTiles()}
        </div>
        {bundles?.length > 0 && canWrite && (
          <AddNew handleClick={showCreateBundleModal} text={t('bundles.add_context_bundle')} />
        )}
      </SettingsContent>
    </PageContentWrapper>
  );
};

export default Bundles;
